import { getMonthKey, getWeekDays } from './dateUtils';

// For SpendSummary/MediaSpendVsContributionGraph
export function transformWeeklyToMonthlySeries(
    xAxisData: string[],
    seriesData: {
        [key: string]: { data: { [key: string]: number }; color: string };
    },
    period?: string,
) {
    if (
        period === 'monthly' &&
        xAxisData.length > 0 &&
        xAxisData.every((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ) {
        const monthlyKeysSet = new Set<string>();
        const newSeriesData: typeof seriesData = {};
        Object.entries(seriesData).forEach(([seriesName, { data, color }]) => {
            const monthlyAgg: Record<string, number> = {};
            Object.entries(data).forEach(([weekPeriod, value]) => {
                const weekDays = getWeekDays(weekPeriod);
                const daysPerMonth: Record<string, number> = {};
                for (const day of weekDays) {
                    const monthKey = getMonthKey(day);
                    daysPerMonth[monthKey] = (daysPerMonth[monthKey] || 0) + 1;
                }
                for (const [monthKey, dayCount] of Object.entries(
                    daysPerMonth,
                )) {
                    monthlyKeysSet.add(monthKey);
                    if (!monthlyAgg[monthKey]) {
                        monthlyAgg[monthKey] = 0;
                    }
                    monthlyAgg[monthKey] += (value * dayCount) / 7;
                }
            });
            newSeriesData[seriesName] = { data: monthlyAgg, color };
        });
        return {
            xAxisData: Array.from(monthlyKeysSet).sort((a, b) =>
                a.localeCompare(b),
            ),
            seriesData: newSeriesData,
        };
    }
    return { xAxisData, seriesData };
}

// For ModelContributions/ContributionAreaGraph and CampaignOptimization/ContributionSeasonalityBarChart
export function transformWeeklyToMonthlyContributionAndSpend(
    xAxisData: string[],
    seriesData: {
        [key: string]: {
            data: { [key: string]: { contribution: number; spend: number } };
            color: string;
        };
    },
    period?: string,
) {
    if (
        (period === 'monthly' || period === undefined) &&
        xAxisData.length > 0 &&
        xAxisData.every((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ) {
        const monthlyKeysSet = new Set<string>();
        const newSeriesData: typeof seriesData = {};
        Object.entries(seriesData).forEach(([seriesName, { data, color }]) => {
            const monthlyAgg: Record<
                string,
                { contribution: number; spend: number }
            > = {};

            Object.entries(data).forEach(([weekPeriod, value]) => {
                const weekDays = getWeekDays(weekPeriod);
                const daysPerMonth: Record<string, number> = {};
                for (const day of weekDays) {
                    const monthKey = getMonthKey(day);
                    daysPerMonth[monthKey] = (daysPerMonth[monthKey] || 0) + 1;
                }
                for (const [monthKey, dayCount] of Object.entries(
                    daysPerMonth,
                )) {
                    monthlyKeysSet.add(monthKey);
                    if (!monthlyAgg[monthKey]) {
                        monthlyAgg[monthKey] = { contribution: 0, spend: 0 };
                    }
                    monthlyAgg[monthKey]!.contribution +=
                        (value.contribution * dayCount) / 7;
                    monthlyAgg[monthKey]!.spend += (value.spend * dayCount) / 7;
                }
            });
            newSeriesData[seriesName] = { data: monthlyAgg, color };
        });
        return {
            xAxisData: Array.from(monthlyKeysSet).sort((a, b) =>
                a.localeCompare(b),
            ),
            seriesData: newSeriesData,
        };
    }
    return { xAxisData, seriesData };
}

export function transformWeeklyToMonthlyRecord(
    xAxisData: string[],
    record: Record<string, number>,
    period?: string,
): Record<string, number> {
    if (
        (period === 'monthly' || period === undefined) &&
        xAxisData.length > 0 &&
        xAxisData.every((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ) {
        const monthlyAgg: Record<string, number> = {};
        Object.entries(record).forEach(([weekPeriod, value]) => {
            const weekDays = getWeekDays(weekPeriod);
            const daysPerMonth: Record<string, number> = {};
            for (const day of weekDays) {
                const monthKey = getMonthKey(day);
                daysPerMonth[monthKey] = (daysPerMonth[monthKey] || 0) + 1;
            }
            for (const [monthKey, dayCount] of Object.entries(daysPerMonth)) {
                if (!monthlyAgg[monthKey]) {
                    monthlyAgg[monthKey] = 0;
                }
                monthlyAgg[monthKey] += (value * dayCount) / 7;
            }
        });
        const sorted: Record<string, number> = {};
        Object.keys(monthlyAgg)
            .sort((a, b) => a.localeCompare(b))
            .forEach((key) => {
                sorted[key] = monthlyAgg[key]!;
            });
        return sorted;
    }
    return record;
}

function getDaysPerMonth(period: string) {
    const weekDays = getWeekDays(period);
    const daysPerMonth: Record<string, number> = {};
    for (const day of weekDays) {
        const monthKey = getMonthKey(day);
        daysPerMonth[monthKey] = (daysPerMonth[monthKey] || 0) + 1;
    }

    return daysPerMonth;
}

const aggregateWeeklyToMonthly = (
    weeklyRows: Array<{ period: string; kpi: number; residual: number }>,
) => {
    const monthlyMap: Record<
        string,
        { kpi: number; residual: number; count: number }
    > = {};
    for (const row of weeklyRows) {
        const daysPerMonth = getDaysPerMonth(row.period);
        for (const [monthKey, dayCount] of Object.entries(daysPerMonth)) {
            if (!monthlyMap[monthKey]) {
                monthlyMap[monthKey] = {
                    kpi: 0,
                    residual: 0,
                    count: 0,
                };
            }
            monthlyMap[monthKey]!.kpi += (row.kpi * dayCount) / 7;
            monthlyMap[monthKey]!.residual += (row.residual * dayCount) / 7;
            monthlyMap[monthKey]!.count += dayCount;
        }
    }

    return Object.entries(monthlyMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, agg]) => ({
            period: month,
            kpi: agg.kpi,
            residual: agg.residual,
        }));
};

// For TitleDashboard/ModelVsActual
export function transformWeeklyToMonthlyModelVsActual(data: any) {
    if (!data || data.period !== 'monthly') return data;

    const isWeekly = data.modelData.every((row: any) =>
        /^\d{4}-\d{2}-\d{2}$/.test(row.period),
    );
    if (!isWeekly) return data;

    const modelDataMonthly = aggregateWeeklyToMonthly(data.modelData);
    const actualDataMonthly = aggregateWeeklyToMonthly(data.actualData);
    const residuals = modelDataMonthly.map((r) => r.residual);
    const minResidual = Math.min(...residuals);
    const maxResidual = Math.max(...residuals);

    return {
        ...data,
        modelData: modelDataMonthly,
        actualData: actualDataMonthly,
        minResidual,
        maxResidual,
    };
}
